import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useToast } from '../context/ToastContext';
import { usePlan, LIMITES } from '../context/PlanContext';
import Logo from '../components/Logo';
import { BookOpen, Lock, Bell, Building2 } from 'lucide-react';

async function carregarPerfis(userIds) {
  if (!userIds.length) return {};
  const { data } = await supabase.from('perfis').select('id, nome, username, slug, foto_url').in('id', userIds);
  const map = {};
  (data || []).forEach(p => { map[p.id] = p; });
  return map;
}

function ClubePublico() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isPago, openUpgrade } = usePlan();

  const [clube, setClube] = useState(null);
  const [membros, setMembros] = useState([]);
  const [livros, setLivros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [usuario, setUsuario] = useState(null);
  const [perfilUsuario, setPerfilUsuario] = useState(null);
  const [minhaEntrada, setMinhaEntrada] = useState(null);
  const [solicitando, setSolicitando] = useState(false);
  const [perfisDonos, setPerfisDonos] = useState({});
  const [avisos, setAvisos] = useState(new Set());
  const [enviandoAviso, setEnviandoAviso] = useState(null);
  const [modalEmprestimo, setModalEmprestimo] = useState(null);
  const [prazo, setPrazo] = useState(14);
  const [mensagemEmprestimo, setMensagemEmprestimo] = useState('');
  const [enviandoEmprestimo, setEnviandoEmprestimo] = useState(false);

  useEffect(() => { carregar(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  async function carregar() {
    setCarregando(true);
    const { data: { user } } = await supabase.auth.getUser();
    setUsuario(user);

    const { data: clubeData } = await supabase
      .from('clubes').select('*').eq('slug', slug).maybeSingle();
    if (!clubeData) { setCarregando(false); return; }
    setClube(clubeData);

    const [{ data: membrosRaw }, { data: livrosRaw }] = await Promise.all([
      supabase.from('clube_membros').select('id, role, user_id, status').eq('clube_id', clubeData.id).eq('status', 'aprovado'),
      supabase.from('clube_livros').select('id, livros(id, titulo, autor, estrelas, disponivel, capa_url, user_id)').eq('clube_id', clubeData.id),
    ]);

    const perfisMap = await carregarPerfis((membrosRaw || []).map(m => m.user_id).filter(Boolean));
    setMembros((membrosRaw || []).map(m => ({ ...m, perfil: perfisMap[m.user_id] || null })));
    setLivros(livrosRaw || []);

    const ownerIds = [...new Set((livrosRaw || []).map(cl => cl.livros?.user_id).filter(Boolean))];
    if (ownerIds.length > 0) {
      const { data: perfisData } = await supabase.from('perfis').select('id, nome, username').in('id', ownerIds);
      const map = {};
      (perfisData || []).forEach(p => { map[p.id] = p; });
      setPerfisDonos(map);
    }

    if (user) {
      const [{ data: entrada }, { data: perfilData }, { data: avisosAtivos }] = await Promise.all([
        supabase.from('clube_membros').select('id, status, role')
          .eq('clube_id', clubeData.id).eq('user_id', user.id).maybeSingle(),
        supabase.from('perfis').select('nome').eq('id', user.id).maybeSingle(),
        supabase.from('avisos_disponibilidade').select('livro_id')
          .eq('user_id', user.id).eq('ativo', true),
      ]);
      setMinhaEntrada(entrada); // pode ter status 'bloqueado'
      setPerfilUsuario(perfilData);
      setAvisos(new Set((avisosAtivos || []).map(a => a.livro_id)));
    }
    setCarregando(false);
  }

  async function handleSolicitar() {
    if (!usuario) {
      localStorage.setItem('redirect_after_login', window.location.pathname);
      navigate('/login');
      return;
    }
    if (!isPago) {
      const { count } = await supabase.from('clube_membros').select('*', { count: 'exact', head: true })
        .eq('user_id', usuario.id).eq('status', 'aprovado');
      if (count >= LIMITES.clubes) { openUpgrade('clubes'); return; }
    }
    setSolicitando(true);
    const { error } = await supabase.from('clube_membros').insert({
      clube_id: clube.id,
      user_id: usuario.id,
      status: 'pendente',
      role: 'membro',
    });
    if (!error) {
      setMinhaEntrada({ status: 'pendente', role: 'membro' });
      showToast('Solicitação enviada! Aguarde aprovação.', 'success');

      const { data: adminEntry } = await supabase
        .from('clube_membros')
        .select('user_id')
        .eq('clube_id', clube.id)
        .eq('role', 'admin')
        .eq('status', 'aprovado')
        .limit(1)
        .maybeSingle();
      if (adminEntry?.user_id && adminEntry.user_id !== usuario.id) {
        await supabase.from('notificacoes').insert({
          user_id: adminEntry.user_id,
          tipo: 'solicitacao_clube',
          lida: false,
        });
      }
    } else {
      showToast('Erro ao solicitar entrada.', 'error');
    }
    setSolicitando(false);
  }

  function abrirModalEmprestimo(cl) {
    if (!usuario) {
      localStorage.setItem('redirect_after_login', window.location.pathname);
      navigate('/login');
      return;
    }
    const donoId = cl.livros?.user_id;
    const dono = donoId ? perfisDonos[donoId] : null;
    setModalEmprestimo({ livro: cl.livros, donoId, donoNome: dono?.nome || '', donoUsername: dono?.username || '' });
    setPrazo(14);
    setMensagemEmprestimo('');
  }

  async function handleSolicitarEmprestimo() {
    if (!modalEmprestimo) return;
    setEnviandoEmprestimo(true);
    if (usuario) {
      const { data: meuPerfil } = await supabase.from('perfis').select('emprestimo_bloqueado').eq('id', usuario.id).maybeSingle();
      if (meuPerfil?.emprestimo_bloqueado) {
        showToast('Sua conta esta bloqueada por inadimplencia. Devolva o livro pendente para desbloquear.', 'error');
        setEnviandoEmprestimo(false);
        return;
      }
    }
    const nomeLeitor = perfilUsuario?.nome || usuario?.user_metadata?.nome || usuario?.email?.split('@')[0] || '';
    const { data: emprestimo, error } = await supabase.from('emprestimos').insert({
      livro_id: modalEmprestimo.livro.id,
      leitor_user_id: usuario.id,
      leitor_nome: nomeLeitor,
      leitor_email: usuario.email,
      mensagem: mensagemEmprestimo || null,
      prazo_dias: prazo,
      status: 'pendente',
    }).select().single();
    if (!error && emprestimo && modalEmprestimo.donoId) {
      await supabase.from('notificacoes').insert({
        user_id: modalEmprestimo.donoId,
        emprestimo_id: emprestimo.id,
        tipo: 'nova_solicitacao',
        lida: false,
      });
    }
    setEnviandoEmprestimo(false);
    if (!error) {
      setModalEmprestimo(null);
      showToast('Solicitação enviada!', 'success');
    } else {
      showToast('Erro ao enviar solicitação.', 'error');
    }
  }

  async function handleAvisar(livroId) {
    if (!usuario || avisos.has(livroId)) return;
    setEnviandoAviso(livroId);
    await supabase.from('avisos_disponibilidade').insert({
      livro_id: livroId,
      user_id: usuario.id,
      ativo: true,
    });
    setAvisos(prev => new Set([...prev, livroId]));
    setEnviandoAviso(null);
  }

  if (carregando) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <p className="text-sm text-ink-400">Carregando...</p>
      </div>
    );
  }

  if (!clube) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center px-4">
          <div className="w-14 h-14 rounded-[18px] flex items-center justify-center mb-4" style={{background:'#EDE8FC'}}>
            <Building2 size={26} strokeWidth={1.5} color="#5B3CE4" />
          </div>
          <p className="text-sm font-medium text-ink-900 mb-1">Clube não encontrado</p>
          <p className="text-xs text-ink-400">O link pode estar errado ou o clube foi removido.</p>
        </div>
      </div>
    );
  }

  const isAprovado = minhaEntrada?.status === 'aprovado';
  const isPendente = minhaEntrada?.status === 'pendente';
  const isRejeitado = minhaEntrada?.status === 'rejeitado';
  const isBloqueado = minhaEntrada?.status === 'bloqueado';
  const isPrivado = clube?.publico === false;

  return (
    <div className="min-h-screen bg-paper">
      {usuario && (
        <div className="bg-white border-b border-line-soft px-4 pb-2.5 pt-safe-nav">
          <button onClick={() => navigate('/home')} className="text-xs text-ink-400 hover:text-ink-500">
            ← Voltar ao acervo
          </button>
        </div>
      )}
      {modalEmprestimo && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4" style={{background:'rgba(0,0,0,0.3)'}}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-sm">
            <p className="text-sm font-medium text-ink-900 mb-1">Solicitar empréstimo</p>
            <p className="text-xs text-ink-400 mb-4 truncate">
              {modalEmprestimo.livro.titulo}
              {modalEmprestimo.donoUsername && (
                <span className="ml-1 text-ink-300">· @{modalEmprestimo.donoUsername}</span>
              )}
            </p>
            <div className="flex flex-col gap-3 mb-4">
              <div>
                <label className="text-xs text-ink-400 mb-1 block">Prazo desejado</label>
                <select value={prazo} onChange={e => setPrazo(parseInt(e.target.value))}
                  className="w-full border border-line-soft rounded-xl px-4 py-2.5 text-sm bg-paper outline-none focus:border-line">
                  <option value={7}>1 semana</option>
                  <option value={14}>2 semanas</option>
                  <option value={21}>3 semanas</option>
                  <option value={30}>1 mês</option>
                  <option value={0}>Sem prazo definido</option>
                </select>
              </div>
              <div>
                <label className="text-xs text-ink-400 mb-1 block">Mensagem <span className="text-ink-300">(opcional)</span></label>
                <textarea value={mensagemEmprestimo} onChange={e => setMensagemEmprestimo(e.target.value)}
                  placeholder="Oi! Posso pegar emprestado?" rows={2}
                  className="w-full border border-line-soft rounded-xl px-4 py-2.5 text-sm bg-paper outline-none focus:border-line resize-none" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSolicitarEmprestimo} disabled={enviandoEmprestimo}
                className="flex-1 py-2.5 text-xs bg-brand text-white rounded-xl hover:bg-brand-dark transition-colors disabled:opacity-50">
                {enviandoEmprestimo ? 'Enviando...' : 'Enviar solicitação'}
              </button>
              <button onClick={() => setModalEmprestimo(null)}
                className="flex-1 py-2.5 text-xs border border-line text-ink-500 rounded-xl hover:bg-paper transition-colors">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-6">

        {/* Cabeçalho do clube */}
        <div className="rounded-2xl overflow-hidden mb-5" style={{background:'linear-gradient(135deg,#5B3CE4,#3F27B0)'}}>
          {clube.capa_url && (
            <div className="w-full" style={{height: 140, position: 'relative'}}>
              <img src={clube.capa_url} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0" style={{background:'linear-gradient(to top, rgba(63,39,176,0.85) 0%, rgba(63,39,176,0.2) 100%)'}} />
            </div>
          )}
          <div className="p-5 flex items-start gap-4">
            {clube.foto_url && (
              <div className="w-14 h-14 rounded-2xl overflow-hidden flex-shrink-0 border-2 mt-0.5" style={{borderColor:'rgba(255,255,255,0.25)'}}>
                <img src={clube.foto_url} alt="" className="w-full h-full object-cover" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white text-xl font-bold">{clube.nome}</p>
              {clube.descricao && (
                <p className="text-sm mt-1 leading-relaxed" style={{color: 'rgba(255,255,255,0.7)'}}>{clube.descricao}</p>
              )}
              <div className="flex gap-5 mt-3">
                <div>
                  <p className="text-white text-base font-semibold">{membros.length}</p>
                  <p className="text-xs" style={{color: 'rgba(255,255,255,0.45)'}}>membro{membros.length !== 1 ? 's' : ''}</p>
                </div>
                <div>
                  <p className="text-white text-base font-semibold">{livros.length}</p>
                  <p className="text-xs" style={{color: 'rgba(255,255,255,0.45)'}}>livro{livros.length !== 1 ? 's' : ''}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Ação de entrada */}
        {isBloqueado ? (
          <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5">
            <p className="text-xs text-red-600">Você foi bloqueado deste clube.</p>
          </div>
        ) : isPrivado && !isAprovado && !isPendente ? (
          <div className="bg-paper border border-line-soft rounded-xl px-4 py-3 mb-5">
            <p className="text-xs text-ink-500 flex items-center gap-1.5"><Lock size={11} />Este clube é privado. Apenas membros convidados podem entrar.</p>
          </div>
        ) : (
          <>
            {!minhaEntrada && (
              <button onClick={handleSolicitar} disabled={solicitando}
                className="w-full bg-brand text-white py-3 rounded-xl text-sm font-medium hover:bg-brand-dark transition-colors disabled:opacity-50 mb-5">
                {solicitando ? 'Enviando...' : usuario ? 'Pedir para entrar' : 'Entre para solicitar'}
              </button>
            )}
            {isRejeitado && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-red-600">Sua solicitação não foi aprovada.</p>
              </div>
            )}
            {isPendente && (
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-amber-700">⏳ Solicitação aguardando aprovação do admin.</p>
              </div>
            )}
            {isAprovado && (
              <div className="bg-paper border border-line-soft rounded-xl px-4 py-3 mb-5">
                <p className="text-xs text-ink-500">Você é membro deste clube.</p>
              </div>
            )}
          </>
        )}

        {/* Membros */}
        {membros.length > 0 && (
          <div className="mb-5">
            <p className="text-sm font-medium text-ink-900 mb-3">Membros</p>
            <div className="bg-white border border-line-soft rounded-xl overflow-hidden">
              {membros.map((m, i) => (
                <div
                  key={m.id}
                  onClick={() => m.perfil?.slug && navigate(`/biblioteca/${m.perfil.slug}`)}
                  className={`flex items-center gap-3 px-4 py-3 ${i < membros.length - 1 ? 'border-b border-gray-50' : ''} ${m.perfil?.slug ? 'cursor-pointer hover:bg-paper transition-colors' : ''}`}
                >
                  <div className="w-9 h-9 rounded-full bg-paper-2 flex items-center justify-center text-sm font-medium text-ink-500 flex-shrink-0 overflow-hidden">
                    {m.perfil?.foto_url
                      ? <img src={m.perfil.foto_url} alt="" className="w-full h-full object-cover" />
                      : (m.perfil?.nome || '?').charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-ink-900">{m.perfil?.nome || 'Usuário'}</p>
                    {m.perfil?.username && <p className="text-xs text-ink-400">@{m.perfil.username}</p>}
                  </div>
                  {m.role === 'admin' && (
                    <span className="text-xs bg-ink-900 text-white px-2 py-0.5 rounded-full flex-shrink-0">Admin</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Livros do clube */}
        {livros.length > 0 && (
          <div className="mb-5">
            <p className="text-sm font-medium text-ink-900 mb-3">Livros do clube</p>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
              {livros.map(cl => {
                const livro = cl.livros;
                if (!livro) return null;
                const meuLivro = livro.user_id === usuario?.id;
                return (
                  <div key={cl.id} className="bg-white border border-line-soft rounded-xl overflow-hidden">
                    <div className="h-20 bg-paper flex items-center justify-center text-2xl overflow-hidden">
                      {livro.capa_url
                        ? <img src={livro.capa_url} alt="" className="w-full h-full object-cover" />
                        : <BookOpen size={18} color="#C4BCDA" strokeWidth={1} />}
                    </div>
                    <div className="p-2.5">
                      <p className="text-xs font-medium text-ink-900 leading-tight line-clamp-2">{livro.titulo}</p>
                      <p className="text-xs text-ink-400 mt-0.5 truncate">{livro.autor}</p>
                      {!meuLivro && (() => {
                        const dono = perfisDonos[livro.user_id];
                        const label = dono?.username ? `@${dono.username}` : dono?.nome;
                        return label ? <p className="text-xs text-ink-300 mt-0.5 truncate">{label}</p> : null;
                      })()}
                      <div className="flex mt-1.5">
                        {[1,2,3,4,5].map(s => (
                          <span key={s} className={`text-xs ${s <= (livro.estrelas || 0) ? 'text-amber-400' : 'text-ink-300'}`}>★</span>
                        ))}
                      </div>
                      {meuLivro ? (
                        <span className="inline-block text-xs px-1.5 py-0.5 rounded-full mt-1.5 bg-paper-2 text-brand font-medium">
                          Seu livro
                        </span>
                      ) : livro.disponivel ? (
                        <>
                          <span className="inline-block text-xs px-1.5 py-0.5 rounded-full mt-1.5 bg-paper-2 text-ink-500">
                            Disponível
                          </span>
                          {isAprovado && (
                            <button onClick={() => abrirModalEmprestimo(cl)}
                              className="w-full mt-1.5 py-1.5 text-xs bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors">
                              Pedir emprestado
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <span className="inline-block text-xs px-1.5 py-0.5 rounded-full mt-1.5 bg-paper-2 text-ink-400">
                            Indisponível
                          </span>
                          {usuario && (
                            <button
                              onClick={() => handleAvisar(livro.id)}
                              disabled={avisos.has(livro.id) || enviandoAviso === livro.id}
                              className="w-full mt-1.5 py-1.5 text-xs border border-line text-ink-500 rounded-lg hover:bg-paper transition-colors disabled:opacity-60 disabled:cursor-default">
                              <span className="flex items-center justify-center gap-1"><Bell size={11} />{avisos.has(livro.id) ? 'Avisarei você' : 'Avise-me'}</span>
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="mt-8 text-center">
          <Logo size="sm" />
        </div>
      </div>
    </div>
  );
}

export default ClubePublico;
